import { IonButton, IonItem, IonLabel, IonList, IonPage, IonRadio, IonRadioGroup, useIonModal } from '@ionic/react';
import {
	AdminActionReasonsLookup,
	AdminActionsTakenLookup,
	Comment,
	DirectMessage,
	FullAdminAction,
	FullAuthored,
	FullPost,
	Post
} from 'model';
import { useLazyGetPostAdminQuery } from '../../api/PostApi';
import { Header } from '../../components/Header';
import { IconButton } from '../../components/IconButton';

import { Fragment } from 'react';
import { useLazyGetCommentAdminQuery } from '../../api/CommentApi';
import { AuthorHeader } from '../../components/AuthorHeader';
import { BigImageContainer } from '../../components/BigImageContainer';
import { Select } from '../../components/Forms/Select';
import { TextArea } from '../../components/Forms/TextArea';
import { PostContent } from '../../components/PostContent';
import { PostContentsSummary } from '../../components/PostContentsSummary';
import { PostContentsToHTML } from '../../components/PostContentsToHTML';
import { IconArrowBack, IconCheck } from '../../components/QuirkIcons';
import { QuirkIonContent } from '../../components/QuirkIonContent';
import { useUserHasRole } from '../../hooks/useUserHasRole';
import { UIAdminActionsTakenLookup, UIReportReasons } from '../../model/report';
import { AdminOnly } from './AdminOnly';
import { ActionFormValuesType } from './ReportList';
import { ReporterListByDocument } from './ReporterListByDocument';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../../styles/posts.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import sharedstyles from '../../styles/Shared.module.css';
import styles from './ReportAction.module.css';
import { useLazyGetDirectMessageQuery } from '../../api/DirectMessageApi.js';
import { ReceivedMessageItem } from '../../components/ReceivedMessageItem.js';

export const ReportAction: React.FC<{
	selectedItem: FullAdminAction;
	onDismiss: () => {};
	actionFormValues: ActionFormValuesType;
	setActionValues: (
		// eslint-disable-next-line no-undef
		e: React.FormEvent<HTMLIonRadioGroupElement>,
		id: string | undefined,
		type: string | undefined
	) => void;
	resolveReports: (authorId: string | undefined, adminReportId: string) => void;
}> = ({ onDismiss, selectedItem, resolveReports, setActionValues, actionFormValues }) => {
	const admin = useUserHasRole('admin');
	const [postQuery, postQueryResult] = useLazyGetPostAdminQuery();
	const [commentQuery, commentQueryResult] = useLazyGetCommentAdminQuery();
	const [messageQuery, messageQueryResult] = useLazyGetDirectMessageQuery();
	let document: FullAuthored<Comment> | FullPost | DirectMessage | null | undefined = null;

	if (selectedItem.documentType === 'post') {
		document = postQueryResult.data;
		!document &&
			!postQueryResult.isFetching &&
			!postQueryResult.isLoading &&
			!postQueryResult.error &&
			postQuery(selectedItem.document);
	} else if (selectedItem.documentType === 'comment') {
		document = commentQueryResult.data;
		!document &&
			!commentQueryResult.isFetching &&
			!commentQueryResult.isLoading &&
			!commentQueryResult.error &&
			!document &&
			commentQuery(selectedItem.document);
	} else if (selectedItem.documentType === 'directMessage') {
		document = messageQueryResult.data;
		!document &&
			!messageQueryResult.isFetching &&
			!messageQueryResult.isLoading &&
			!messageQueryResult.error &&
			messageQuery(selectedItem.document);
	}
	const userId =
		selectedItem.documentType !== 'directMessage'
			? (document as FullAuthored<Comment | Post>)?.author
			: (document as DirectMessage)?.from;
	const reportAction = () => {
		resolveReports(userId, selectedItem._id);
		onDismiss();
	};

	// Report filter page
	const [presentReportFilter, dismissReporterListByDocument] = useIonModal(ReporterListByDocument, {
		documentId: document?._id,
		onDismiss: () => dismissReporterListByDocument()
	});
	return (
		<IonPage>
			<Header
				title="Reported content"
				left={<IconButton icon={<IconArrowBack />} onClick={onDismiss} />}
				right={
					admin ? (
						<IconButton
							testId="DoneButton"
							label="Done"
							fill="solid"
							icon={<IconCheck />}
							onClick={selectedItem.actionTaken === 'created' ? reportAction : onDismiss}
						/>
					) : (
						<div></div>
					)
				}
			/>
			<QuirkIonContent>
				<AdminOnly>
					<div>
						{document && (
							<div className={sharedstyles.contentWrapper}>
								<div className={styles.allReportsButton}>
									<IonButton
										data-testid="ViewReportersButton"
										color={'primary'}
										fill="outline"
										size="small"
										className="radius8"
										onClick={() => presentReportFilter()}
										expand="block"
									>
										View {selectedItem.totalReports} reporters
									</IonButton>
								</div>
								{selectedItem.actionTaken === 'created' ? (
									<div>
										<div className={styles.label}>Select Action:</div>
										<IonList>
											<IonRadioGroup
												value={actionFormValues.actionTaken}
												name="actionTaken"
												onClick={e => setActionValues(e, document?._id, document?.type)}
											>
												{Object.keys(UIAdminActionsTakenLookup).map((action, i) => {
													return (
														<Fragment key={action}>
															<IonItem
																className={styles.ionItem}
																data-testid={`${action}Button`}
															>
																<IonLabel>
																	{Object.values(UIAdminActionsTakenLookup)[i]}
																</IonLabel>
																<IonRadio slot="end" value={action}></IonRadio>
															</IonItem>
														</Fragment>
													);
												})}
											</IonRadioGroup>
										</IonList>
										<div className={styles.fieldContainer}>
											<Select
												id="reason"
												placeholder="Add a reason"
												label="Select Reason"
												value={actionFormValues.reason}
												name="reason"
												options={UIReportReasons}
												onChange={e => setActionValues(e, document?._id, document?.type)}
											/>
										</div>
										<div className={styles.messageContainer}>
											<div className={styles.label}>Explanation:</div>
											<TextArea
												name="description"
												testId="explanation"
												placeholder="Add an explanation message"
												onChange={e => setActionValues(e, document?._id, document?.type)}
											/>
										</div>
									</div>
								) : (
									<div>
										<div className={styles.label}>Action Taken:</div>
										<div className={styles.actionTaken}>
											{AdminActionsTakenLookup[selectedItem.actionTaken]}
										</div>
										<div className="space20" />
										<div className={styles.label}>Reason:</div>
										<div className={styles.actionTaken}>
											{AdminActionReasonsLookup[selectedItem.reason]}
										</div>
										<div className="space20" />
										<div className={styles.label}>Explanation:</div>
										<div className={styles.actionTaken}>{selectedItem.description}</div>
									</div>
								)}

								<div className={styles.postPreviewContainer}>
									{document.type !== 'directMessage' && (
										<div className={postStyles.postName}>
											<AuthorHeader authoredItem={document} />
										</div>
									)}
									{document.type === 'post' && (
										<div className={styles.bottomPadding}>
											<PostContent
												post={document}
												children={<PostContentsSummary post={document} allowExpand={true} />}
											/>

											{document.images && document.images.length > 0 && (
												<div className={postStyles.postImageContainer}>
													<BigImageContainer post={document} images={document.images} />
												</div>
											)}
										</div>
									)}
									{document.type === 'comment' && (
										<>
											<PostContentsToHTML>{document.comment}</PostContentsToHTML>
										</>
									)}
									{document.type === 'directMessage' && (
										<ReceivedMessageItem item={document} readOnly />
									)}
								</div>
							</div>
						)}
					</div>
				</AdminOnly>
			</QuirkIonContent>
		</IonPage>
	);
};
